import { Radio, RadioChangeEvent, Space } from "antd"
import { SizeType } from "antd/es/config-provider/SizeContext"
import { AuthorFilterProvider } from "components/providers/CustomFilter.provider"
import { capitalizeFirstLetter } from "helpers/utils.helper"
import { InputSearch } from "components/ui/inputs/InputSearch"
import { useUsers } from "hooks/api/user/user.hook"
import { Skeleton } from "components/ui/skeleton/Skeleton"
import { useState, useEffect, useRef } from "react"
import { useFillesTableStore } from "store/table.store"
import type { FilterDropdownProps } from "antd/es/table/interface"
import type { TUser } from "types/user.type"
import type { TOnChangeInput } from "types/default.type"
import styles from "./CustomAuthorFilter.module.scss"

const { searchWrapper, usersWrapper, skeletonWrapper } = styles

interface FilterOption {
	text: string
	value: string
}

interface Props extends FilterDropdownProps {
	filteredOptions: FilterOption[]
	size?: SizeType
	dataIndex: string
	clearFilters: () => void | undefined
}

export const CustomAuthorFilter = ({
	filteredOptions,
	selectedKeys,
	setSelectedKeys,
	clearFilters,
	confirm,
	dataIndex,
	size = "large",
}: Props) => {
	const { data: users, isPending: usersPending } = useUsers()
	const [filteredUsers, setFilteredUsers] = useState<TUser[]>([])

	const { setFilter, tableParams } = useFillesTableStore()
	const stringSlectedKey = selectedKeys[0]
	const initialized = useRef(false)

	// Owner radio change handle
	const radioChangeHandle = (e: RadioChangeEvent) => {
		const value = e.target.value
		setSelectedKeys([value])
		setFilter({ owner: value, user: null }) // Set owner filter and remove user filter
		localStorage.setItem("owner", value)
		localStorage.removeItem("user") // Remove user from localStorage to avoid conflict with user filter
		confirm()
	}

	// Users radio change handle
	const usersRadioChangeHandle = (e: RadioChangeEvent) => {
		const value = e.target.value
		clearFilters()
		setFilter({ user: value, owner: "all" }) // Set user filter and remove owner filter
		localStorage.setItem("user", value)
		localStorage.removeItem("owner") // Remove owner from localStorage to avoid conflict with owner filter
		confirm() // Apply filter
	}

	// Sort users by second name alphabetically
	const sortedUsers = users?.sort((a: TUser, b: TUser) => {
		if (a.secondName < b.secondName) return -1
		if (a.secondName > b.secondName) return 1
		return 0
	})

	// Initialize filteredUsers with sortedUsers when users data is loaded
	useEffect(() => {
		if (sortedUsers && !initialized.current) {
			setFilteredUsers(sortedUsers)
			initialized.current = true
		}
	}, [sortedUsers])

	// Search handle
	const searchHandle = (e: TOnChangeInput) => {
		const searchValue = e.target.value.toLowerCase()
		const filtered =
			sortedUsers?.filter((user: TUser) => {
				const fullName = `${user.name} ${user.secondName}`
				return fullName.toLowerCase().includes(searchValue)
			}) || []
		setFilteredUsers(filtered)
	}

	// Clear filters handle
	const cleaFitersHandle = () => {
		clearFilters()
		confirm()
		setFilter({ owner: "all", user: null })
		localStorage.removeItem("owner")
		localStorage.removeItem("user")
	}

	return (
		<div className="customFilter">
			<div className="filterHeader">
				<span className="filterName">{capitalizeFirstLetter(dataIndex)}</span>
				<button className="cleanButton" onClick={cleaFitersHandle}>
					Clean
				</button>
			</div>
			<AuthorFilterProvider>
				<div data-testid="owner-filter">
					<Radio.Group value={stringSlectedKey} onChange={radioChangeHandle} size={size}>
						<Space direction="horizontal">
							{filteredOptions
								? filteredOptions.map((option) => (
										<Radio key={option.value} value={option.value}>
											{option.text}
										</Radio>
									))
								: null}
						</Space>
					</Radio.Group>
				</div>
			</AuthorFilterProvider>
			{usersPending ? (
				<div className={skeletonWrapper}>
					<Skeleton rows={7} />
				</div>
			) : (
				<div className={searchWrapper}>
					<InputSearch onChange={searchHandle} />
					<div data-testid="user-filter" className={usersWrapper}>
						<AuthorFilterProvider>
							<Radio.Group value={Number(tableParams.filter.user)} onChange={usersRadioChangeHandle}>
								<Space direction="vertical">
									{filteredUsers?.map((user: TUser) => (
										<Radio key={user.id} value={user.id}>
											{`${user.name} ${user.secondName}`}
										</Radio>
									))}
								</Space>
							</Radio.Group>
						</AuthorFilterProvider>
					</div>
				</div>
			)}
		</div>
	)
}
